import { isPlainObject } from "./isPlainObject";

/**
 * 检查给定的值是否为空
 *
 * - 如果给定的值是字符串，检查它是否为空字符串
 * - 如果给定的值是数组、Map 或 Set，检查其大小是否为 0
 * - 如果给定的值是类数组对象，检查其长度是否为 0
 * - 如果给定的值是对象，检查它是否是没有属性的空对象
 * - 原始值（字符串、布尔值、数字或 bigints）被视为空
 *
 * @param value - 要检查的值
 * @returns 如果值为空返回 true，否则返回 false
 * @example
 * ```ts
 * isEmpty(null); // => true
 * isEmpty(undefined); // => true
 * isEmpty(''); // => true
 * isEmpty([]); // => true
 * isEmpty({}); // => true
 * isEmpty(new Map()); // => true
 * isEmpty(new Set()); // => true
 * isEmpty(0); // => true
 * isEmpty(false); // => true
 * isEmpty(true); // => true
 * isEmpty(123); // => true
 * isEmpty('text'); // => false
 * isEmpty([1, 2]); // => false
 * isEmpty({ a: 1 }); // => false
 * ```
 */
export function isEmpty(value: unknown): boolean {
  // null 和 undefined 被视为空
  if (value == null) {
    return true;
  }

  // 原始值（字符串除外）被视为空
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return true;
  }

  // 字符串检查
  if (typeof value === "string") {
    return value.length === 0;
  }

  // 数组检查
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  // Map 和 Set 检查
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  // 类数组对象检查
  if (
    value !== null &&
    typeof value === "object" &&
    "length" in value &&
    typeof (value as { length: unknown }).length === "number"
  ) {
    return (value as { length: number }).length === 0;
  }

  // 普通对象检查
  if (isPlainObject(value)) {
    return Object.keys(value).length === 0;
  }

  // 其他类型（函数、日期、正则表达式等）不被视为空
  return false;
}
